import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  GroupsIcon,
  Table,
  useDefaultSorting,
} from '@bp/ui-components';
import { Row } from '@tanstack/react-table';
import { FragmentType } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useTranslation } from 'react-i18next';
import { BpPermissionChecker } from 'utils/PermissionChecker';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { UseGroupsTableFragment, useGroupsTable } from '../hooks/useGroupsTable';
import { GroupTableType } from '../types/types';

type CooperationGroupsTableProps = {
  query: FragmentType<typeof UseGroupsTableFragment> | undefined;
  permissions: BpPermissionChecker | null;
  onNavigate: (uuid: string) => void;
  onEditorsEdit: (groupUuid: string) => void;
  onViewersEdit: (groupUuid: string) => void;
};

export const CooperationGroupsTable = ({
  query,
  permissions,
  onNavigate,
  onEditorsEdit,
  onViewersEdit,
}: CooperationGroupsTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const isDesktop = useIsDesktop();

  const { data, columns } = useGroupsTable({
    query,
    columns: ['name', 'organization', 'admins', 'schoolYear', 'nextcloud', 'threema'],
    onNavigate,
  });

  const { sorting, onSortingChange } = useDefaultSorting();

  // for Collaboration Groups only Members are editable
  const canEditMembers = (row: Row<GroupTableType>) =>
    permissions?.canUpdateCourse({
      uuid: row.original.uuid,
      managedBy: row.original.managedBy,
      organization: { uuid: pimAuthClaims.getOrganizationUuid() },
    }) && row.original.organizationUuid !== pimAuthClaims.getOrganizationUuid();

  function getDropdownMenuItems(row: Row<GroupTableType>): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];

    if (canEditMembers(row)) {
      items.push({
        label: t('groups.editEditors'),
        onClick: () => onEditorsEdit(row.original.uuid),
      });
      items.push({
        label: t('groups.editViewers'),
        onClick: () => onViewersEdit(row.original.uuid),
      });
    }

    return items;
  }

  return (
    <Table<GroupTableType>
      isDesktop={isDesktop}
      data-cy='courseList'
      showActionBar
      isOnWhite={false}
      showBorderRadius
      showShadow
      canScroll
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      actionBarSettings={{
        showExpertFilter: true,
      }}
      emptyStateSettings={{
        icon: <GroupsIcon />,
        padding: 'xl',
      }}
      columns={columns}
      data={data}
      lastColWidth='32px'
      lastCol={(row) => {
        const items = getDropdownMenuItems(row);
        const disabled = items.length === 0;
        return (
          <Dropdown
            disabled={disabled}
            trigger={<Button disabled={disabled} hierarchy='ghost' icon={<DotsVerticalIcon />} />}
          >
            <DropdownMenu data={items} />
          </Dropdown>
        );
      }}
    />
  );
};
